import * as React from "react"
import { cn } from "cn"

/**
 * A single semantic dot, sized and shaped like one of the brand dots.
 *
 * Colour alone is never sufficient — every StatusDot requires a `label`, which
 * is exposed to assistive technology and, by default, rendered as visible text
 * beside the dot. Pass `labelHidden` only when the surrounding row already
 * states the status in words.
 */
const tones = {
  neutral: "bg-muted-foreground",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
} as const

function StatusDot({
  tone = "neutral",
  label,
  labelHidden = false,
  pulse = false,
  className,
  ...props
}: Omit<React.ComponentProps<"span">, "children"> & {
  tone?: keyof typeof tones
  label: string
  labelHidden?: boolean
  pulse?: boolean
}) {
  return (
    <span
      data-slot="status-dot"
      data-tone={tone}
      className={cn("inline-flex items-center gap-2 text-sm", className)}
      {...props}
    >
      <span className="relative flex size-2 shrink-0">
        {pulse && (
          <span
            aria-hidden="true"
            className={cn(
              "absolute inline-flex size-full animate-ping rounded-full opacity-60",
              tones[tone]
            )}
          />
        )}
        <span className={cn("relative inline-flex size-2 rounded-full", tones[tone])} />
      </span>
      <span className={cn(labelHidden && "sr-only")}>{label}</span>
    </span>
  )
}

export { StatusDot }
